import React, { useRef } from 'react';
import { useModalFocus } from './useModalFocus';
import { formatDuration } from './helpVideos';

/**
 * How-to videos panel: one CARD per video — title, one-line description,
 * "· 2 min" and a play button. The card loads NOTHING from Google: no
 * thumbnail, no script, no iframe; a styled placeholder with a play glyph
 * stands in for a thumbnail. Pressing play hands (video, cardButton) to the
 * provider, which mounts HelpVideoPlayer above this panel.
 */
export default function HelpVideosPanel({ videos, onPlay, onClose, returnFocusTo }) {
  const cardRef = useRef(null);
  const closeRef = useRef(null);
  useModalFocus({ cardRef, initialRef: closeRef, onEscape: onClose, returnTo: returnFocusTo });

  const list = Array.isArray(videos) ? videos : [];

  return (
    <div
      className="modal-overlay help-videos-overlay"
      data-app-modal="help-videos"
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px',
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-videos-title"
        className="modal-content help-videos-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', padding: '24px', borderRadius: '12px',
          width: '100%', maxWidth: '640px', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 id="help-videos-title" style={{ margin: 0, color: '#334155', fontSize: '1.4rem', textAlign: 'left' }}>How-to videos</h2>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Close how-to videos"
            title="Close"
            style={{ background: 'transparent', border: 'none', fontSize: '1.6em', cursor: 'pointer', color: '#94a3b8', minWidth: '44px', minHeight: '44px' }}
          >
            &times;
          </button>
        </div>
        <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.95em' }}>
          Short walkthroughs. Videos play here only after you press play.
        </p>
        <div className="help-video-list" style={{ overflowY: 'auto', flex: 1, display: 'grid', gap: '12px' }}>
          {list.map((v) => {
            const duration = formatDuration(v.durationSec);
            return (
              <article key={v.id || v.videoId} className="help-video-card" data-video-id={v.id || v.videoId}>
                <div className="help-video-thumb" aria-hidden="true">
                  <span className="help-video-thumb-glyph">&#9654;</span>
                </div>
                <div className="help-video-meta">
                  <h3 className="help-video-title">{v.title}</h3>
                  {v.description ? <p className="help-video-desc">{v.description}</p> : null}
                  <div className="help-video-actions">
                    <button
                      type="button"
                      className="help-video-play"
                      onClick={(e) => onPlay(v, e.currentTarget)}
                      aria-label={`Play ${v.title}${duration ? ` (${duration})` : ''}`}
                    >
                      &#9654; Play
                    </button>
                    {duration ? <span className="help-video-duration"> &middot; {duration}</span> : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
